import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { formatRelativeTime } from '../utils/time';
import './AdminChats.css';

interface ChatSummary {
  chat: { id: string; userId: string; status: string };
  user: { id: string; name: string; email: string };
  lastMessage?: { content: string; senderRole: string; createdAt: string };
}

interface ChatMessage {
  id: string;
  senderRole: 'user' | 'admin' | 'assistant';
  content: string;
  createdAt: string;
}

const AdminChats: React.FC = () => {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [smartReplies, setSmartReplies] = useState<string[]>([]);
  const initialLoadRef = useRef(true);
  const lastMessageRef = useRef<Record<string, { createdAt?: string; senderRole?: string }>>({});
  const wsRef = useRef<WebSocket | null>(null);
  const [wsReady, setWsReady] = useState(false);
  const [chatPage, setChatPage] = useState(1);
  const chatsPerPage = 20;

  const playPing = useCallback(() => {
    const AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gainNode.gain.value = 0.08;

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.12);
    oscillator.onended = () => context.close();
  }, []);

  const fetchChats = useCallback(async () => {
    const response = await api.get('/admin/chats');
    const nextChats = response.data?.chats || [];
    if (!initialLoadRef.current) {
      const hasNewMessage = nextChats.some((chat: ChatSummary) => {
        const current = chat.lastMessage?.createdAt;
        const currentRole = chat.lastMessage?.senderRole;
        const previous = lastMessageRef.current[chat.chat.id]?.createdAt;
        if (!current || current === previous) return false;
        return currentRole === 'user';
      });
      if (hasNewMessage) {
        playPing();
      }
    }

    lastMessageRef.current = nextChats.reduce(
      (acc: Record<string, { createdAt?: string; senderRole?: string }>, chat: ChatSummary) => {
        acc[chat.chat.id] = {
          createdAt: chat.lastMessage?.createdAt,
          senderRole: chat.lastMessage?.senderRole,
        };
        return acc;
      },
      {}
    );

    initialLoadRef.current = false;
    setChats(nextChats);
  }, [playPing]);

  const fetchSmartReplies = useCallback((_chatId: string) => {
    setSmartReplies([]);
  }, []);

  const fetchMessages = useCallback(
    async (chatId: string) => {
      setMessagesLoading(true);
      try {
        const response = await api.get(`/admin/chats/${chatId}/messages`);
        setMessages(response.data?.messages || []);
        fetchSmartReplies(chatId);
      } finally {
        setMessagesLoading(false);
      }
    },
    [fetchSmartReplies]
  );

  useEffect(() => {
    const init = async () => {
      try {
        await fetchChats();
      } finally {
        setLoading(false);
      }
    };
    init();

    if (wsReady) return;
    const interval = setInterval(fetchChats, 10000);
    return () => clearInterval(interval);
  }, [fetchChats, wsReady]);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}/api`;
    const base = apiUrl.replace(/\/api\/?$/, '');
    const wsUrl = base.replace(/^http/, 'ws') + '/ws';
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;
    setWsReady(false);

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'identify', role: 'admin' }));
    };

    socket.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'admin_ready') {
          setWsReady(true);
          if (selectedChatId) {
            socket.send(JSON.stringify({ type: 'subscribe', chatId: selectedChatId }));
          }
          return;
        }
        if (data.type === 'chat_update') {
          const chatId = data.chatId;
          if (chatId && data.message) {
            if (chatId === selectedChatId) {
              setMessages(prev => [...prev, data.message]);
            }
            setChats(prev =>
              prev.map(chatItem =>
                chatItem.chat.id === chatId
                  ? {
                      ...chatItem,
                      lastMessage: {
                        content: data.message.content,
                        senderRole: data.message.senderRole,
                        createdAt: data.message.createdAt,
                      },
                    }
                  : chatItem
              )
            );
            lastMessageRef.current[chatId] = {
              createdAt: data.message.createdAt,
              senderRole: data.message.senderRole,
            };
            if (!initialLoadRef.current && data.message.senderRole === 'user') {
              playPing();
            }
          }
        }
        if (data.type === 'admin_message' && data.message) {
          if (data.clientId) {
            setMessages(prev =>
              prev.map(message => (message.id === data.clientId ? data.message : message))
            );
          } else {
            setMessages(prev => [...prev, data.message]);
          }
          if (selectedChatId) {
            setChats(prev =>
              prev.map(chatItem =>
                chatItem.chat.id === selectedChatId
                  ? {
                      ...chatItem,
                      lastMessage: {
                        content: data.message.content,
                        senderRole: data.message.senderRole,
                        createdAt: data.message.createdAt,
                      },
                    }
                  : chatItem
              )
            );
            lastMessageRef.current[selectedChatId] = {
              createdAt: data.message.createdAt,
              senderRole: data.message.senderRole,
            };
          }
        }
      } catch (error) {
        // ignore
      }
    };

    socket.onclose = () => {
      setWsReady(false);
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [fetchChats, playPing, selectedChatId]);

  useEffect(() => {
    if (!selectedChatId && chats.length > 0) {
      setSelectedChatId(chats[0].chat.id);
    }
  }, [chats, selectedChatId]);

  useEffect(() => {
    setChatPage(1);
  }, [chats.length]);

  const totalChatPages = Math.max(1, Math.ceil(chats.length / chatsPerPage));
  const chatPageStart = (chatPage - 1) * chatsPerPage;
  const paginatedChats = chats.slice(chatPageStart, chatPageStart + chatsPerPage);

  useEffect(() => {
    if (!selectedChatId) return;
    fetchMessages(selectedChatId);
    if (wsRef.current && wsReady) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', chatId: selectedChatId }));
    }
  }, [fetchMessages, selectedChatId, wsReady]);

  useEffect(() => {
    const heartbeat = () => api.post('/admin/chats/heartbeat').catch(() => undefined);
    heartbeat();
    const interval = setInterval(heartbeat, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSend = async (override?: string) => {
    if (!selectedChatId) return;
    const content = (override ?? input).trim();
    if (!content) return;

    const tempId = `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const tempMessage: ChatMessage = {
      id: tempId,
      senderRole: 'admin',
      content,
      createdAt: new Date().toISOString(),
    };

    setSending(true);
    setInput('');
    setMessages(prev => [...prev, tempMessage]);
    try {
      if (wsRef.current && wsReady) {
        wsRef.current.send(
          JSON.stringify({ type: 'admin_message', chatId: selectedChatId, content, clientId: tempId })
        );
      } else {
        const response = await api.post(`/admin/chats/${selectedChatId}/messages`, { content });
        if (response.data?.message) {
          setMessages(prev =>
            prev.map(message => (message.id === tempId ? response.data.message : message))
          );
        } else {
          await fetchMessages(selectedChatId);
        }
        await fetchChats();
        fetchSmartReplies(selectedChatId);
      }
    } catch (error) {
      setMessages(prev => prev.filter(message => message.id !== tempId));
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container admin-chats-page">
      <div className="admin-chats-header">
        <div>
          <h1>Support Chats</h1>
          <p>Respond to member inquiries in real time.</p>
        </div>
        <span className={`admin-chats-status ${wsReady ? 'online' : 'offline'}`}>
          {wsReady ? 'Connected' : 'Connecting'}
        </span>
      </div>

      <div className="admin-chats-layout">
        <aside className="chat-list">
          {loading ? (
            <div className="chat-list-skeleton">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="chat-skeleton-card">
                  <div className="chat-skeleton-line title" />
                  <div className="chat-skeleton-line meta" />
                  <div className="chat-skeleton-line meta short" />
                </div>
              ))}
            </div>
          ) : chats.length === 0 ? (
            <div className="chat-list-empty">
              <div className="chat-empty-icon">💬</div>
              <h3>No chats yet</h3>
              <p>When members reach out, their conversations will appear here.</p>
            </div>
          ) : (
            paginatedChats.map(chat => (
              <button
                key={chat.chat.id}
                className={`chat-list-item ${selectedChatId === chat.chat.id ? 'active' : ''}`}
                onClick={() => setSelectedChatId(chat.chat.id)}
              >
                <div className="chat-list-name">{chat.user.name}</div>
                <div className="chat-list-email">{chat.user.email}</div>
                <div className="chat-list-preview">
                  {chat.lastMessage?.content || 'No messages yet'}
                </div>
              </button>
            ))
          )}
          {chats.length > chatsPerPage && (
            <div className="chat-list-pagination">
              <button
                className="pagination-btn"
                onClick={() => setChatPage(prev => Math.max(1, prev - 1))}
                disabled={chatPage === 1}
              >
                Prev
              </button>
              <span className="pagination-label">
                {chatPage} / {totalChatPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setChatPage(prev => Math.min(totalChatPages, prev + 1))}
                disabled={chatPage === totalChatPages}
              >
                Next
              </button>
            </div>
          )}
        </aside>

        <section className="chat-thread">
          {selectedChatId ? (
            <>
              <div className="chat-thread-messages">
                {messagesLoading ? (
                  <div className="chat-thread-skeleton">
                    {[0, 1, 2].map((item) => (
                      <div key={item} className="chat-thread-skeleton-row">
                        <div className="chat-thread-skeleton-line short" />
                        <div className="chat-thread-skeleton-line" />
                      </div>
                    ))}
                  </div>
                ) : (
                  messages.map(message => (
                    <div key={message.id} className={`chat-thread-row ${message.senderRole}`}>
                      <div className="chat-thread-bubble">{message.content}</div>
                      <div className="chat-thread-time">
                        {formatRelativeTime(message.createdAt)}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {smartReplies.length > 0 && (
                <div className="chat-smart-replies">
                  {smartReplies.map(reply => (
                    <button
                      key={reply}
                      type="button"
                      className="chat-smart-reply"
                      onClick={() => handleSend(reply)}
                      disabled={sending}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
              <div className="chat-thread-input">
                <textarea
                  placeholder="Type your reply..."
                  value={input}
                  onChange={event => setInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      const trimmed = input.trim();
                      if (!trimmed) return;
                      setInput('');
                      handleSend(trimmed);
                    }
                  }}
                  rows={2}
                  disabled={sending}
                />
                <button
                  className="chat-icon-button chat-icon-send"
                  onClick={() => handleSend()}
                  disabled={sending || !input.trim()}
                  aria-label="Send message"
                  title="Send"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M2 21l20-9L2 3v7l14 2-14 2v7z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="chat-thread-empty">
              <div className="chat-empty-icon">👋</div>
              <h3>Select a chat</h3>
              <p>Choose a member to view and reply to their messages.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminChats;
